/**
 * Polygon utility functions for geographic operations
 */

import {extractCoordinatesFromWKTFlat} from '@app/utils/wkt-parser';

/**
 * Check if a point is inside a polygon using ray-casting algorithm
 * @param point Point with lat/lon coordinates
 * @param polygon Array of vertices with lat/lon coordinates
 * @returns true if point is inside polygon, false otherwise
 */
export function pointInPolygon(
  point: { lat: number; lon: number },
  polygon: Array<{ lat: number; lon: number }>
): boolean {
  // Ray-casting algorithm
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].lat, yi = polygon[i].lon;
    const xj = polygon[j].lat, yj = polygon[j].lon;

    const intersect = ((yi > point.lon) !== (yj > point.lon)) &&
      (point.lat < (xj - xi) * (point.lon - yi) / (yj - yi) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
}

/**
 * Check if two polygons are equivalent for matching purposes
 * Ignores vertex order and starting point; uses tolerance of 150 meters
 * @param a First polygon vertices
 * @param b Second polygon vertices
 * @returns true if polygons are equivalent, false otherwise
 */
export function polygonsAreEquivalent(
  a: Array<{ lat: number; lon: number }>,
  b: Array<{ lat: number; lon: number }>
): boolean {
  const thresholdMeters = 150;

  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length === 0 || b.length === 0) return false;

  // Normalize: remove closing vertex if equal to first (within tiny tolerance)
  const normalize = (pts: Array<{ lat: number; lon: number }>) => {
    if (pts.length > 1) {
      const first = pts[0];
      const last = pts[pts.length - 1];
      if (Math.abs(first.lat - last.lat) < 1e-6 && Math.abs(first.lon - last.lon) < 1e-6) {
        return pts.slice(0, pts.length - 1);
      }
    }
    return pts.slice();
  };

  const na = normalize(a);
  const nb = normalize(b);

  if (na.length === 0 || nb.length === 0) return false;

  // Haversine distance (meters)
  const toRad = (deg: number) => deg * Math.PI / 180;
  const distanceMeters = (p1: { lat: number; lon: number }, p2: { lat: number; lon: number }) => {
    const R = 6371000; // Earth radius in meters
    const dLat = toRad(p2.lat - p1.lat);
    const dLon = toRad(p2.lon - p1.lon);
    const lat1 = toRad(p1.lat);
    const lat2 = toRad(p2.lat);
    const aHarv = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(aHarv), Math.sqrt(1 - aHarv));
    return R * c;
  };

  // For each point in small set, ensure there is a close point in the other set
  const allPointsClose = (from: Array<{ lat: number; lon: number }>, to: Array<{ lat: number; lon: number }>) => {
    for (const p of from) {
      let minDist = Infinity;
      for (const q of to) {
        const d = distanceMeters(p, q);
        if (d < minDist) minDist = d;
        if (minDist <= thresholdMeters) break;
      }
      if (minDist > thresholdMeters) return false;
    }
    return true;
  };

  // Check both directions to be symmetric
  const ok1 = allPointsClose(na, nb);
  const ok2 = allPointsClose(nb, na);

  return ok1 && ok2;
}


export function extractCoordinates(polygonData: string): Array<[number, number]> {
  if (!polygonData) return [];

  try {
    // Check if it's WKT format (starts with POLYGON, MULTIPOLYGON, etc.)
    const trimmedData = polygonData.trim();
    if (trimmedData.startsWith('POLYGON') || trimmedData.startsWith('MULTIPOLYGON')) {
      return extractCoordinatesFromWKTFlat(trimmedData);
    } else {
      // Try parsing as GeoJSON
      try {
        const geoJson = JSON.parse(polygonData);
        return extractCoordinatesFromGeoJSON(geoJson);
      } catch (parseError) {
        // If parsing fails, try as WKT anyway
        return extractCoordinatesFromWKTFlat(trimmedData);
      }
    }
  } catch (error) {
    // Error extracting coordinates
    console.warn('Error extracting coordinates:', error);
    return [];
  }
}

export  function  extractCoordinatesFromGeoJSON(geoJson: any): Array<[number, number]> {
  if (!geoJson) return [];

let coordinates: Array<[number, number]> = [];

try {
  // Handle different GeoJSON types
  if (geoJson.type === 'Polygon' && geoJson.coordinates) {
    // For Polygon, use the outer ring (first coordinate array)
    const outerRing = geoJson.coordinates[0];
    if (Array.isArray(outerRing)) {
      coordinates = outerRing.map((coord: any) => {
        // GeoJSON format is [lng, lat], convert to [lat, lng]
        if (Array.isArray(coord) && coord.length >= 2) {
          return [coord[1], coord[0]] as [number, number];
        }
        return [0, 0] as [number, number];
      });
    }
  } else if (geoJson.type === 'MultiPolygon' && geoJson.coordinates) {
    // For MultiPolygon, use the first polygon's outer ring
    const firstPolygon = geoJson.coordinates[0];
    if (Array.isArray(firstPolygon) && Array.isArray(firstPolygon[0])) {
      const outerRing = firstPolygon[0];
      coordinates = outerRing.map((coord: any) => {
        // GeoJSON format is [lng, lat], convert to [lat, lng]
        if (Array.isArray(coord) && coord.length >= 2) {
          return [coord[1], coord[0]] as [number, number];
        }
        return [0, 0] as [number, number];
      });
    }
  } else if (geoJson.geometry) {
    // If it's a Feature, extract from geometry
    return extractCoordinatesFromGeoJSON(geoJson.geometry);
  }
} catch (error) {
  console.warn('Error extracting GeoJSON coordinates:', error);
}

return coordinates;
}


export  function  olCalculateAreaFromPolygon(polygonJson: string, openLayerInstance:any): number {
  if (!polygonJson) return 0;

  try {
    const coordinates = JSON.parse(polygonJson);
    if (!coordinates || !Array.isArray(coordinates) || coordinates.length < 3) return 0;

    const firstCoord = coordinates[0];
    const isProjected = Math.abs(firstCoord[0]) > 180 || Math.abs(firstCoord[1]) > 90;

    const realCoords = isProjected
      ? coordinates.map((coord: [number, number]) => openLayerInstance.proj.toLonLat(coord))
      : coordinates;

    let area = 0;
    const n = realCoords.length - 1;

    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      area += realCoords[i][0] * realCoords[j][1];
      area -= realCoords[j][0] * realCoords[i][1];
    }

    area = Math.abs(area) / 2;
    const areaInSquareMeters = area * (111320 * 111320);
    const areaInHectares = areaInSquareMeters / 10000;

    return Number(areaInHectares.toFixed(2));
  } catch (error) {
    return 0;
  }
}

export function olCalculateAreaFromPolygonInHectares(coords: any[], openLayerInstance:any) {
  const firstCoord = coords[0];
  const isProjected = Math.abs(firstCoord[0]) > 180 || Math.abs(firstCoord[1]) > 90;
  const realCoords = isProjected
    ? coords.map((coord: [number, number]) => openLayerInstance.proj.toLonLat(coord))
    : coords;

  let area = 0;
  const n = realCoords.length - 1;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    area += realCoords[i][0] * realCoords[j][1];
    area -= realCoords[j][0] * realCoords[i][1];
  }
  area = Math.abs(area) / 2;
  const areaInHectares = (area * (111320 * 111320)) / 10000;
  return areaInHectares;
}

export function  olParsePolygonJson(polygonJson: string): [number, number][] | null {
  if (!polygonJson || !polygonJson.startsWith('POLYGON')) {
    console.error('Invalid POLYGON format');
    return null;
  }

  try {
    const coordinatesString = polygonJson
      .replace('POLYGON ((', '')
      .replace('))', '');

    const coordinatePairs = coordinatesString.split(',');

    return coordinatePairs.map(pair => {
      const [lon, lat] = pair.trim().split(' ').map(Number);
      return [lon, lat] as [number, number];
    });
  } catch (error) {
    console.error('Error parsing POLYGON JSON:', error);
    return null;
  }
}


/**
 * Vérifie si deux segments se croisent
 */
export function  doSegmentsIntersect(
  p1: number[], p2: number[],
  p3: number[], p4: number[]
): boolean {
  const ccw = (A: number[], B: number[], C: number[]): boolean => {
    return (C[1] - A[1]) * (B[0] - A[0]) > (B[1] - A[1]) * (C[0] - A[0]);
  };

  // Vérifier si les segments partagent un point (adjacents)
  const sharePoint = (
    (p1[0] === p3[0] && p1[1] === p3[1]) ||
    (p1[0] === p4[0] && p1[1] === p4[1]) ||
    (p2[0] === p3[0] && p2[1] === p3[1]) ||
    (p2[0] === p4[0] && p2[1] === p4[1])
  );

  if (sharePoint) {
    return false; // Les segments adjacents ne sont pas considérés comme se croisant
  }

  return (
    ccw(p1, p3, p4) !== ccw(p2, p3, p4) &&
    ccw(p1, p2, p3) !== ccw(p1, p2, p4)
  );
}

/**
 * Vérifie si un polygone se croise lui-même
 */
export function isSelfIntersecting(coordinates: number[][]): boolean {
  const n = coordinates.length;

  // Besoin d'au moins 4 points pour avoir une auto-intersection
  if (n < 4) {
    return false;
  }

  // Vérifier chaque paire de segments non adjacents
  for (let i = 0; i < n - 1; i++) {
    for (let j = i + 2; j < n - 1; j++) {
      // Ignorer le premier et dernier segment s'ils sont adjacents (fermeture du polygone)
      if (i === 0 && j === n - 2) {
        continue;
      }

      if (doSegmentsIntersect(
        coordinates[i], coordinates[i + 1],
        coordinates[j], coordinates[j + 1]
      )) {
        return true;
      }
    }
  }

  return false;
}

